"use client";

import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { List, Map as MapIcon, X } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { MOBILE_BREAKPOINT, useMobileMatchMedia } from "@/hooks/use-mobile";
import { useResetAboveWidth } from "@/components/responsive/breakpoint-gate";

interface MobileMapToggleProps {
  children: ReactNode;
  pillLabel?: string;
  overlayTitle?: string;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function MobileMapToggle({
  children,
  pillLabel = "Map",
  overlayTitle,
  className,
  open: controlledOpen,
  onOpenChange,
}: MobileMapToggleProps) {
  const isMobile = useMobileMatchMedia();
  const isDesktop = isMobile === false;
  const isControlled = controlledOpen !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = isControlled ? controlledOpen : uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean) => {
      setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );
  const titleId = useId();
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const overlayRef = useRef<HTMLDivElement | null>(null);

  const close = useCallback(() => setOpen(false), [setOpen]);

  useResetAboveWidth(MOBILE_BREAKPOINT, close);

  useEffect(() => {
    if (!open || isDesktop) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const triggerNode = triggerRef.current;
    closeBtnRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        close();
        return;
      }
      if (event.key !== "Tab" || !overlayRef.current) return;

      const focusable = overlayRef.current.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = original;
      document.removeEventListener("keydown", handleKeyDown);
      triggerNode?.focus();
    };
  }, [open, close, isDesktop]);

  if (isDesktop) {
    return <div className={className}>{children}</div>;
  }

  return (
    <>
      {isControlled ? null : (
        <Button
          variant="outline"
          size="default"
          ref={triggerRef}
          type="button"
          onClick={() => setOpen(true)}
          className="mx-4 my-3 w-[calc(100%-2rem)]"
          aria-label={`Open ${pillLabel}`}
        >
          <MapIcon className="h-4 w-4" aria-hidden="true" />
          {pillLabel}
        </Button>
      )}

      {open ? (
        <div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="fixed inset-0 z-50 bg-background flex flex-col"
        >
          <div className="flex items-center justify-between border-b border-border bg-background px-4 py-3">
            <p
              id={titleId}
              className="text-sm font-semibold text-foreground truncate"
            >
              {overlayTitle ?? pillLabel}
            </p>
            <Button
              variant="outline"
              size="sm"
              ref={closeBtnRef}
              type="button"
              onClick={close}
              className={cn(
                "rounded-full px-3 text-xs",
                "active:scale-95 transition-transform",
              )}
              aria-label="Close map"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Close
            </Button>
          </div>
          <div className="group/overlay flex-1 min-h-0 relative">
            <div className="absolute inset-0 [&_>_*:not(button)]:!h-full [&_>_*:not(button)]:!w-full">
              {children}
            </div>
            <Button
              variant="primary"
              size="sm"
              type="button"
              onClick={close}
              aria-label="Show listings list"
              className={cn(
                "absolute left-1/2 -translate-x-1/2 z-10",
                "bottom-[max(env(safe-area-inset-bottom),16px)]",
                "rounded-full px-4 text-sm font-semibold",
                "shadow-lg",
                "active:scale-95 transition-transform",
                "max-md:group-has-[.listing-map-popup]/overlay:bg-popover max-md:group-has-[.listing-map-popup]/overlay:text-foreground max-md:group-has-[.listing-map-popup]/overlay:hover:bg-muted",
              )}
            >
              <List className="h-4 w-4" aria-hidden="true" />
              Show list
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );
}
